/**
 * AppShell — 响应式应用框架
 *
 * 桌面（≥768px）：左侧图标导航栏（64px）
 * 移动（<768px）：底部导航栏（含登录入口）
 *
 * 路由驱动：当前页由 useLocation 判断，导航用 <Link>
 */

import { Link, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BookOpen, FileText, Sparkles, Settings as SettingsIcon, RotateCcw, LogIn } from 'lucide-react';
import type { Page } from '../stores/app';
import { useAuthStore } from '../stores/auth';

interface NavItem {
  page: Page;
  path: string;
  icon: typeof BookOpen;
  labelKey: string;
}

const navItems: NavItem[] = [
  { page: 'shelf', path: '/shelf', icon: BookOpen, labelKey: 'nav.shelf' },
  { page: 'reader', path: '/reader', icon: FileText, labelKey: 'nav.reader' },
  { page: 'vocab', path: '/hump', icon: Sparkles, labelKey: 'nav.vocab' },
  { page: 'review', path: '/review', icon: RotateCcw, labelKey: 'nav.review' },
  { page: 'settings', path: '/settings', icon: SettingsIcon, labelKey: 'nav.settings' },
];

/** 从当前路径推断高亮的导航项 */
function getActivePage(pathname: string): Page | null {
  if (pathname.startsWith('/shelf')) return 'shelf';
  if (pathname.startsWith('/reader')) return 'reader';
  if (pathname.startsWith('/hump')) return 'vocab';
  if (pathname.startsWith('/review')) return 'review';
  if (pathname.startsWith('/settings')) return 'settings';
  return null;
}

export function AppShell() {
  const location = useLocation();
  const { t } = useTranslation();
  const activePage = getActivePage(location.pathname);
  const { user } = useAuthStore();

  return (
    <div className="min-h-screen bg-warm-white dark:bg-warm-dark flex">
      {/* 桌面侧栏 */}
      <nav className="hidden md:flex flex-col items-center gap-2 w-16 py-4 border-r border-border-warm dark:border-border-warm-dark bg-white dark:bg-warm-dark shrink-0">
        <Link to="/shelf" className="text-2xl mb-4">🐪</Link>

        {navItems.map((item) => (
          <Link
            key={item.page}
            to={item.path}
            className={`flex flex-col items-center justify-center w-12 h-12 rounded-md transition-colors ${
              activePage === item.page
                ? 'text-oasis bg-oasis/10 border-l-[3px] border-oasis rounded-l-none'
                : 'text-charcoal/60 dark:text-charcoal-light/60 hover:bg-warm-white dark:hover:bg-border-warm-dark'
            }`}
            title={t(item.labelKey)}
          >
            <item.icon className="w-5 h-5" />
          </Link>
        ))}

        {/* 桌面端登录/用户入口 — 底部 */}
        <div className="mt-auto">
          {user ? (
            <Link
              to="/settings/account"
              className="flex items-center justify-center w-12 h-12 rounded-md hover:bg-oasis/10 transition-colors"
              title={user.displayName || user.email}
            >
              <div className="w-7 h-7 rounded-full bg-camel/20 flex items-center justify-center text-sm font-medium text-camel-dark dark:text-camel">
                {user.displayName?.[0] || user.email[0]?.toUpperCase()}
              </div>
            </Link>
          ) : (
            <Link
              to="/auth"
              className="flex items-center justify-center w-12 h-12 rounded-md text-camel hover:bg-camel/10 transition-colors"
              title="登录 / 注册"
            >
              <LogIn className="w-5 h-5" />
            </Link>
          )}
        </div>
      </nav>

      {/* 内容区 */}
      <main className="flex-1 min-w-0 pb-16 md:pb-0">
        <Outlet />
      </main>

      {/* 移动底栏（含登录入口） */}
      <nav className="fixed bottom-0 left-0 right-0 md:hidden flex items-center justify-around bg-white dark:bg-warm-dark border-t border-border-warm dark:border-border-warm-dark pb-[env(safe-area-inset-bottom)] z-30">
        {navItems.slice(0, 4).map((item) => (
          <Link
            key={item.page}
            to={item.path}
            className={`flex flex-col items-center gap-0.5 py-2 px-2 transition-colors ${
              activePage === item.page
                ? 'text-camel'
                : 'text-charcoal/50 dark:text-charcoal-light/50'
            }`}
          >
            <item.icon className="w-5 h-5" />
            <span className="text-[10px]">{t(item.labelKey)}</span>
          </Link>
        ))}

        {/* 移动端第5个：登录或设置 */}
        {user ? (
          <Link
            to="/settings"
            className={`flex flex-col items-center gap-0.5 py-2 px-2 transition-colors ${
              activePage === 'settings'
                ? 'text-camel'
                : 'text-charcoal/50 dark:text-charcoal-light/50'
            }`}
          >
            <SettingsIcon className="w-5 h-5" />
            <span className="text-[10px]">设置</span>
          </Link>
        ) : (
          <Link
            to="/auth"
            className="flex flex-col items-center gap-0.5 py-2 px-2 text-camel"
          >
            <LogIn className="w-5 h-5" />
            <span className="text-[10px]">登录</span>
          </Link>
        )}
      </nav>
    </div>
  );
}
